import React, { createContext, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getValidSession } from '../pages/helpers/sessionUtils';

const VendorAuthContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const normalizeVendor = (vendorInfo) => {
    if (!vendorInfo) return null;
    return {
        ...vendorInfo,
        last_registration_step: Number(vendorInfo.last_registration_step ?? vendorInfo.lastRegistrationStep ?? 0),
        is_impersonated: Boolean(vendorInfo.is_impersonated ?? false)
    };
};

export const VendorAuthProvider = ({ children }) => {
    const [vendor, setVendor] = useState(() => {
        let vendorData = localStorage.getItem('userInfo');
        if (!vendorData) {
            const session = getValidSession();
            if (session?.user) {
                const userData = session.user.user || session.user;
                vendorData = JSON.stringify(normalizeVendor(userData));
                localStorage.setItem('userInfo', vendorData);
            }
        }
        if (vendorData) {
            try {
                return normalizeVendor(JSON.parse(vendorData));
            } catch (e) {
                return null;
            }
        }
        return null;
    });
    const navigate = useNavigate();

    const login = async (token, vendorInfo) => {
        const baseVendor = normalizeVendor(vendorInfo);
        localStorage.setItem('authToken', token);
        localStorage.setItem('token', token);

        let resolvedVendor = baseVendor;
        if (baseVendor?.id) {
            try {
                const response = await axios.get(`${API_BASE_URL}/vendors/${baseVendor.id}/details`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data?.user) {
                    resolvedVendor = normalizeVendor({
                        ...baseVendor,
                        ...response.data.user,
                        is_impersonated: baseVendor.is_impersonated
                    });
                }
            } catch (error) {
                console.error('VendorAuthContext - failed to refresh vendor profile after login:', error);
            }
        }

        localStorage.setItem('userInfo', JSON.stringify(resolvedVendor));
        setVendor(resolvedVendor);
        const step = Number(resolvedVendor?.last_registration_step ?? 0);
        if (resolvedVendor?.is_impersonated) {
            navigate('/admin/dashboard');
        } else if (step < 6) {
            navigate('/admin/kyc-verification');
        } else if (step < 12) {
            navigate('/admin/profile-setup');
        } else {
            navigate('/admin/dashboard');
        }
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        setVendor(null);
        navigate('/login');
    };

    const updateVendor = (vendorInfo) => {
        const normalizedVendor = normalizeVendor(vendorInfo);
        localStorage.setItem('userInfo', JSON.stringify(normalizedVendor));
        setVendor(normalizedVendor);
    };

    return (
        <VendorAuthContext.Provider value={{ vendor, login, logout, updateVendor }}>
            {children}
        </VendorAuthContext.Provider>
    );
};

export const useVendorAuth = () => useContext(VendorAuthContext);

